import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Loader2, Sparkles } from 'lucide-react';
import api from '../services/api';

const ResetPasswordPage = ({ token, isReset = false, onSuccess }) => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [status, setStatus] = useState('idle'); // idle, loading, success, error
    const [message, setMessage] = useState('');

    // Password strength indicators
    const hasMinLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const passwordsMatch = password === confirmPassword && password.length > 0;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!hasMinLength) {
            setStatus('error');
            setMessage('Password must be at least 8 characters');
            return;
        }

        if (!passwordsMatch) {
            setStatus('error');
            setMessage('Passwords do not match');
            return;
        }

        setStatus('loading');

        try {
            const endpoint = isReset ? '/reset-password' : '/set-password';
            await api.post(endpoint, {
                token,
                password: password
            });
            setStatus('success');
        } catch (err) {
            setStatus('error');
            if (err.response?.status === 400) {
                setMessage('This reset link has expired. Please request a new one.');
            } else if (err.response?.status === 404) {
                setMessage('Invalid reset link. Please request a new one.');
            } else {
                setMessage('Something went wrong. Please try again.');
            }
        }
    };

    if (status === 'success') {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="bg-white rounded-3xl p-8 max-w-md w-full shadow-xl text-center"
                >
                    <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle size={40} className="text-emerald-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 mb-2">Password Reset!</h1>
                    <p className="text-slate-500 mb-6">
                        Your password has been successfully updated. You can now log in with your new password.
                    </p>
                    <button
                        onClick={onSuccess}
                        className="w-full py-3 bg-slate-900 text-white rounded-xl font-medium 
                     hover:bg-slate-800 transition-colors"
                    >
                        Go to Login
                    </button>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="bg-white rounded-3xl p-8 max-w-md w-full shadow-xl"
            >
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-yellow-300 to-amber-500 rounded-2xl 
                        flex items-center justify-center mx-auto mb-4 rotate-3">
                        <Sparkles className="text-white w-8 h-8" />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900">{isReset ? 'Reset Your Password' : 'Set New Password'}</h1>
                    <p className="text-slate-500 mt-1">{isReset ? 'Enter your new password below' : 'Choose a strong password for your account'}</p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-5">
                    {/* New Password */}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            New Password
                        </label>
                        <div className="relative">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                                <Lock size={18} />
                            </div>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    setStatus('idle');
                                }}
                                placeholder="Create a strong password"
                                className="w-full pl-12 pr-12 py-3 border border-slate-200 rounded-xl 
                         focus:ring-2 focus:ring-amber-500 focus:border-amber-500 
                         outline-none transition-all text-slate-800"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    {/* Password Strength */}
                    <div className="space-y-2 p-4 bg-slate-50 rounded-xl">
                        <div className={`flex items-center gap-2 text-sm ${hasMinLength ? 'text-emerald-600' : 'text-slate-400'}`}>
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${hasMinLength ? 'border-emerald-500 bg-emerald-100' : 'border-slate-300'}`}>
                                {hasMinLength && <CheckCircle size={10} className="text-emerald-600" />}
                            </div>
                            At least 8 characters
                        </div>
                        <div className={`flex items-center gap-2 text-sm ${hasUppercase ? 'text-emerald-600' : 'text-slate-400'}`}>
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${hasUppercase ? 'border-emerald-500 bg-emerald-100' : 'border-slate-300'}`}>
                                {hasUppercase && <CheckCircle size={10} className="text-emerald-600" />}
                            </div>
                            One uppercase letter
                        </div>
                        <div className={`flex items-center gap-2 text-sm ${hasNumber ? 'text-emerald-600' : 'text-slate-400'}`}>
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${hasNumber ? 'border-emerald-500 bg-emerald-100' : 'border-slate-300'}`}>
                                {hasNumber && <CheckCircle size={10} className="text-emerald-600" />}
                            </div>
                            One number
                        </div>
                    </div>

                    {/* Confirm Password */}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Confirm Password
                        </label>
                        <div className="relative">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                                <Lock size={18} />
                            </div>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => {
                                    setConfirmPassword(e.target.value);
                                    setStatus('idle');
                                }}
                                placeholder="Confirm your password"
                                className={`w-full pl-12 pr-4 py-3 border rounded-xl 
                         focus:ring-2 focus:ring-amber-500 focus:border-amber-500 
                         outline-none transition-all text-slate-800
                         ${confirmPassword && !passwordsMatch ? 'border-red-300' : 'border-slate-200'}
                         ${passwordsMatch ? 'border-emerald-300' : ''}`}
                            />
                        </div>
                        {confirmPassword && !passwordsMatch && (
                            <p className="text-red-500 text-sm mt-1">Passwords don't match</p>
                        )}
                    </div>

                    {/* Error Message */}
                    {status === 'error' && (
                        <div className="flex items-center gap-2 text-red-600 text-sm p-3 bg-red-50 rounded-xl">
                            <AlertCircle size={16} />
                            {message}
                        </div>
                    )}

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={status === 'loading' || !hasMinLength || !passwordsMatch}
                        className="w-full py-3 bg-slate-900 text-white rounded-xl font-medium 
                     hover:bg-slate-800 transition-colors disabled:opacity-50 
                     disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {status === 'loading' ? (
                            <>
                                <Loader2 size={20} className="animate-spin" />
                                Updating...
                            </>
                        ) : (
                            'Set New Password'
                        )}
                    </button>
                </form>
            </motion.div>
        </div>
    );
};

export default ResetPasswordPage;
